import type { PontoDaSerie } from "@base/contracts";
import { EstadoVazio, cn } from "@base/ui";
import { useId, useState } from "react";
import { rotuloDeDiaMes, rotuloDeNumero } from "./textos";

export const MEDIDAS_DO_GRAFICO = {
  largura: 720,
  altura: 220,
  margemX: 56,
  margemTopo: 36,
  margemBase: 40,
} as const;

export type PontoDesenhado = {
  readonly rotulo: string;
  readonly valor: number;
  readonly x: number;
  readonly y: number;
};

function yDoValor(valor: number, min: number, max: number): number {
  const { altura, margemTopo, margemBase } = MEDIDAS_DO_GRAFICO;
  const utilY = altura - margemTopo - margemBase;
  return max === min ? margemTopo + utilY / 2 : margemTopo + utilY * (1 - (valor - min) / (max - min));
}

/** Coordenadas no viewBox. Valor maior fica mais alto (y menor). Série vazia não desenha. */
export function pontosDoGrafico(serie: readonly PontoDaSerie[], extras: readonly number[] = []): readonly PontoDesenhado[] {
  const quantidade = serie.length;
  if (quantidade === 0) return [];
  const min = Math.min(...serie.map((ponto) => ponto.valor), ...extras);
  const max = Math.max(...serie.map((ponto) => ponto.valor), ...extras);
  const { largura, margemX } = MEDIDAS_DO_GRAFICO;
  const utilX = largura - margemX * 2;
  return serie.map((ponto, indice) => {
    const x = quantidade === 1 ? largura / 2 : margemX + (utilX * indice) / (quantidade - 1);
    return { rotulo: ponto.rotulo, valor: ponto.valor, x, y: yDoValor(ponto.valor, min, max) };
  });
}

function caminho(pontos: readonly PontoDesenhado[]): string {
  return pontos
    .map((ponto, indice) => `${indice === 0 ? "M" : "L"}${ponto.x.toFixed(2)} ${ponto.y.toFixed(2)}`)
    .join(" ");
}

/** A API manda a semana como data ISO; o eixo mostra só dia e mês. */
function rotuloDoEixo(rotulo: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(rotulo) ? rotuloDeDiaMes(rotulo) : rotulo;
}

/**
 * Linha da série semanal. Os números ficam numa tabela só para leitor de tela;
 * o desenho repete o rótulo no eixo, escondido da árvore.
 */
export function GraficoDaSerie({
  serie,
  rotulo,
  ultimoEmAlerta = false,
  meta,
  base: baseValor,
}: {
  readonly serie: readonly PontoDaSerie[];
  readonly rotulo: string;
  readonly ultimoEmAlerta?: boolean;
  readonly meta?: number | undefined;
  readonly base?: number | undefined;
}) {
  const id = useId();
  const extras = [meta, baseValor].filter((item): item is number => item != null && item > 0);
  const pontos = pontosDoGrafico(serie, extras);
  const ultimo = pontos.at(-1);
  const [ativo, setAtivo] = useState<number | null>(null);
  if (!ultimo) {
    return <EstadoVazio titulo="Sem pontos nesta série." descricao="Ainda não há semanas para desenhar." />;
  }
  const { largura, altura, margemX, margemTopo, margemBase } = MEDIDAS_DO_GRAFICO;
  const eixo = altura - margemBase;
  const valores = pontos.map((ponto) => ponto.valor);
  const min = Math.min(...valores, ...extras);
  const max = Math.max(...valores, ...extras);
  const corDoUltimo = ultimoEmAlerta ? "fill-destructive" : "fill-primary";
  const apontado = ativo !== null ? pontos[ativo] : null;

  return (
    <div className="min-w-0">
      <svg
        role="img"
        aria-labelledby={id}
        viewBox={`0 0 ${largura} ${altura}`}
        preserveAspectRatio="xMidYMid meet"
        className="block h-auto w-full max-w-full"
        style={{ aspectRatio: `${largura} / ${altura}` }}
      >
        <title id={id}>{rotulo}</title>
        <line x1={margemX - 8} x2={largura - 16} y1={eixo} y2={eixo} className="stroke-border" strokeWidth={1} />
        <line x1={margemX - 8} x2={largura - 16} y1={margemTopo} y2={margemTopo} className="stroke-border/60" strokeWidth={1} />
        <g aria-hidden="true" className="fill-muted-foreground text-xs">
          <text x={margemX - 16} y={eixo + 4} textAnchor="end">
            {rotuloDeNumero(min)}
          </text>
          <text x={margemX - 16} y={margemTopo + 4} textAnchor="end">
            {rotuloDeNumero(max)}
          </text>
        </g>
        {meta != null && meta > 0 ? (
          <g aria-hidden="true">
            <line
              x1={margemX}
              x2={largura - 16}
              y1={yDoValor(meta, min, max)}
              y2={yDoValor(meta, min, max)}
              className="stroke-primary"
              strokeWidth={1.5}
              strokeDasharray="6 5"
            />
            <text x={largura - 16} y={yDoValor(meta, min, max) + 16} textAnchor="end" className="fill-primary text-xs font-semibold">
              Meta {rotuloDeNumero(meta)}
            </text>
          </g>
        ) : null}
        {baseValor != null && baseValor > 0 ? (
          <g aria-hidden="true">
            <line
              x1={margemX}
              x2={largura - 16}
              y1={yDoValor(baseValor, min, max)}
              y2={yDoValor(baseValor, min, max)}
              className="stroke-muted-foreground"
              strokeWidth={1.25}
              strokeDasharray="2 4"
            />
            <text
              x={largura - 80}
              y={yDoValor(baseValor, min, max) - 8}
              textAnchor="end"
              className="fill-muted-foreground text-xs"
            >
              Base {rotuloDeNumero(baseValor)}
            </text>
          </g>
        ) : null}
        {pontos.length > 1 ? (
          <path
            d={caminho(pontos)}
            className="fill-none stroke-foreground"
            strokeWidth={2.25}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
        {pontos.map((ponto, indice) => {
          const eUltimo = indice === pontos.length - 1;
          return (
            <g key={`${ponto.rotulo}-${ponto.x}`}>
              <circle
                cx={ponto.x}
                cy={ponto.y}
                r={eUltimo ? 6 : 3.5}
                className={cn(eUltimo ? corDoUltimo : "fill-foreground", "cursor-pointer")}
                onMouseEnter={() => setAtivo(indice)}
                onMouseLeave={() => setAtivo(null)}
                onFocus={() => setAtivo(indice)}
                onBlur={() => setAtivo(null)}
                tabIndex={0}
              >
                <title>{`${rotuloDoEixo(ponto.rotulo)}: ${rotuloDeNumero(ponto.valor)}`}</title>
              </circle>
              <text
                aria-hidden="true"
                x={ponto.x}
                y={altura - 16}
                textAnchor="middle"
                className={cn("text-xs", eUltimo ? "fill-foreground font-semibold" : "fill-muted-foreground")}
              >
                {rotuloDoEixo(ponto.rotulo)}
              </text>
            </g>
          );
        })}
        {apontado ? (
          <g aria-hidden="true">
            <rect
              x={Math.min(apontado.x - 8, largura - 110)}
              y={apontado.y - 28}
              width={92}
              height={20}
              rx={4}
              className="fill-foreground"
            />
            <text
              x={Math.min(apontado.x - 8, largura - 110) + 46}
              y={apontado.y - 14}
              textAnchor="middle"
              className="fill-background text-xs font-semibold"
            >
              {rotuloDeNumero(apontado.valor)} · {rotuloDoEixo(apontado.rotulo)}
            </text>
          </g>
        ) : (
          <text
            aria-hidden="true"
            x={ultimo.x - 12}
            y={ultimo.y - 10}
            textAnchor="end"
            className={cn("text-[13px] font-bold", corDoUltimo)}
          >
            {rotuloDeNumero(ultimo.valor)}
          </text>
        )}
      </svg>
      <table className="sr-only">
        <caption>{rotulo}</caption>
        <thead>
          <tr>
            <th scope="col">Semana</th>
            <th scope="col">Valor</th>
          </tr>
        </thead>
        <tbody>
          {pontos.map((ponto, indice) => (
            <tr key={`${ponto.rotulo}-${indice}`}>
              <th scope="row">{ponto.rotulo}</th>
              <td>{rotuloDeNumero(ponto.valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
